import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { USER_QUESTIONS } from "@/lib/constants/userQuestions";

export interface UserQuestionAnswer {
  questionId: string;
  answer: 'yes' | 'no' | 'na' | string; // Allow custom string answers for dropdown questions
  note: string;
}

interface UserDeclaredQuestionsProps {
  onAnswersChange: (answers: UserQuestionAnswer[]) => void;
  initialAnswers?: UserQuestionAnswer[];
  onValidationChange?: (isValid: boolean) => void;
}

const UserDeclaredQuestions: React.FC<UserDeclaredQuestionsProps> = ({
  onAnswersChange,
  initialAnswers = [],
  onValidationChange
}) => {
  console.log('🔍 UserDeclaredQuestions: Component mounted with:', { 
    initialAnswers, 
    initialAnswersLength: initialAnswers.length
  });
  
  const [answers, setAnswers] = useState<UserQuestionAnswer[]>(() => {
    // Initialize with existing answers or create empty answers for all questions
    const initialAnswersMap = new Map(initialAnswers.map(a => [a.questionId, a]));
    const defaultAnswers = USER_QUESTIONS.map(q => {
      const existingAnswer = initialAnswersMap.get(q.id);
      if (existingAnswer) return existingAnswer;
      // Default answer based on question type
      if (q.type === 'select') {
        return { questionId: q.id, answer: '', note: '' };
      }
      return { questionId: q.id, answer: 'na' as 'yes' | 'no' | 'na', note: '' };
    });
    console.log('🔍 UserDeclaredQuestions: Initialized answers:', defaultAnswers);
    return defaultAnswers;
  });


  const handleAnswerChange = (questionId: string, answer: 'yes' | 'no' | 'na' | string) => {
    console.log('🔍 UserDeclaredQuestions: Answer changed:', { questionId, answer });
    const newAnswers = answers.map(a => 
      a.questionId === questionId ? { ...a, answer } : a
    );
    console.log('🔍 UserDeclaredQuestions: New answers:', newAnswers);
    setAnswers(newAnswers);
    onAnswersChange(newAnswers);
    
    // Check if all questions are answered (including 'na' as valid, or any string for dropdown questions)
    const allAnswered = newAnswers.every(a => {
      if (!a.answer) return false;
      const question = USER_QUESTIONS.find(q => q.id === a.questionId);
      if (question?.type === 'select') {
        // For dropdown questions, any selected value is valid
        return a.answer.length > 0;
      }
      // For radio questions, must be yes/no/na
      return a.answer === 'yes' || a.answer === 'no' || a.answer === 'na';
    });
    console.log('🔍 UserDeclaredQuestions: All answered:', allAnswered);
    onValidationChange?.(allAnswered);
  };

  const handleNoteChange = (questionId: string, note: string) => {
    const newAnswers = answers.map(a => 
      a.questionId === questionId ? { ...a, note } : a
    );
    setAnswers(newAnswers);
    onAnswersChange(newAnswers);
  };


  // Initialize parent component with default answers on mount
  useEffect(() => {
    console.log('🔍 UserDeclaredQuestions: Calling onAnswersChange with initial answers:', answers);
    onAnswersChange(answers);
  }, []); // Only run on mount

  // Check validation on mount and when answers change
  useEffect(() => {
    console.log('🔍 UserDeclaredQuestions: Validation check:', { 
      answersCount: answers.length, 
      answers: answers
    });
    const allAnswered = answers.every(a => {
      if (!a.answer) return false;
      const question = USER_QUESTIONS.find(q => q.id === a.questionId);
      if (question?.type === 'select') {
        // For dropdown questions, any selected value is valid
        return a.answer.length > 0;
      }
      // For radio questions, must be yes/no/na
      return a.answer === 'yes' || a.answer === 'no' || a.answer === 'na';
    });
    console.log('🔍 UserDeclaredQuestions: Validation result:', { allAnswered });
    onValidationChange?.(allAnswered);
  }, [answers, onValidationChange]);

  const getAnswerColor = (answer: string) => {
    switch (answer) {
      case 'yes': return 'text-green-600';
      case 'no': return 'text-red-600';
      case 'na': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Info className="h-5 w-5 text-blue-600" />
          User Declared Questions
        </CardTitle>
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>MANDATORY:</strong> All {USER_QUESTIONS.length} questions must be answered before proceeding with analysis. 
            You must select "Yes", "No", or "N/A" for each question.
            These answers will be saved along with the AI analysis results.
          </AlertDescription>
        </Alert>
      </CardHeader>
      <CardContent className="space-y-6">

        {(() => { console.log(`Total USER_QUESTIONS count: ${USER_QUESTIONS.length}`); return null; })()}
        {USER_QUESTIONS.map((question, index) => {
          console.log(`Rendering question ${index + 1}: ${question.id}`);
          const currentAnswer = answers.find(a => a.questionId === question.id);
          const isAnswered = question.type === 'select' 
            ? currentAnswer?.answer && currentAnswer.answer.length > 0
            : currentAnswer?.answer && (currentAnswer.answer === 'yes' || currentAnswer.answer === 'no' || currentAnswer.answer === 'na');
          
          return (
            <div key={question.id} className={`space-y-3 p-4 border rounded-lg ${isAnswered ? 'bg-gray-50' : 'bg-red-50 border-red-200'}`}>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {index + 1}. {question.question} <span className="text-red-500">*</span>
                </Label>
                {question.subQuestion && (
                  <p className="text-xs text-gray-600 ml-4 italic">
                    {question.subQuestion}
                  </p>
                )}
              </div>
              
              {question.type === 'select' && question.options ? (
                <Select
                  value={currentAnswer?.answer || ''}
                  onValueChange={(value) => handleAnswerChange(question.id, value)}
                >
                  <SelectTrigger className="w-full md:w-[300px]">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {question.options.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
              <RadioGroup
                value={currentAnswer?.answer || 'na'}
                onValueChange={(value) => handleAnswerChange(question.id, value as 'yes' | 'no' | 'na')}
                className="flex space-x-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id={`${question.id}-yes`} />
                  <Label htmlFor={`${question.id}-yes`} className="text-green-600 font-medium">
                    Yes
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id={`${question.id}-no`} />
                  <Label htmlFor={`${question.id}-no`} className="text-red-600 font-medium">
                    No
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="na" id={`${question.id}-na`} />
                  <Label htmlFor={`${question.id}-na`} className="text-gray-600 font-medium">
                    N/A
                  </Label>
                </div>
              </RadioGroup>
              )}
              
              <div className="space-y-2">
                <Label htmlFor={`${question.id}-note`} className="text-sm">
                  Additional Notes (Optional)
                </Label>
                <Textarea
                  id={`${question.id}-note`}
                  placeholder="Add any additional notes or observations..."
                  value={currentAnswer?.note || ''}
                  onChange={(e) => handleNoteChange(question.id, e.target.value)}
                  className="min-h-[60px]"
                />
              </div>
            </div>
          );
        })}
        
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Summary</h4>
          <div className="grid grid-cols-4 gap-4 text-sm mb-3">
            <div className="text-green-600">
              <span className="font-medium">Yes: </span>
              {answers.filter(a => a.answer === 'yes').length}
            </div>
            <div className="text-red-600">
              <span className="font-medium">No: </span>
              {answers.filter(a => a.answer === 'no').length}
            </div>
            <div className="text-gray-600">
              <span className="font-medium">N/A: </span>
              {answers.filter(a => a.answer === 'na').length}
            </div>
          </div>
          <div className={`text-sm font-medium ${answers.every(a => {
            if (!a.answer) return false;
            const question = USER_QUESTIONS.find(q => q.id === a.questionId);
            if (question?.type === 'select') {
              return a.answer.length > 0;
            }
            return a.answer === 'yes' || a.answer === 'no' || a.answer === 'na';
          }) ? 'text-green-600' : 'text-red-600'}`}>
            {answers.every(a => {
              if (!a.answer) return false;
              const question = USER_QUESTIONS.find(q => q.id === a.questionId);
              if (question?.type === 'select') {
                return a.answer.length > 0;
              }
              return a.answer === 'yes' || a.answer === 'no' || a.answer === 'na';
            })
              ? '✅ All questions answered - Ready to proceed' 
              : `❌ ${answers.filter(a => {
                if (!a.answer) return true;
                const question = USER_QUESTIONS.find(q => q.id === a.questionId);
                if (question?.type === 'select') {
                  return a.answer.length === 0;
                }
                return a.answer !== 'yes' && a.answer !== 'no' && a.answer !== 'na';
              }).length} questions remaining - Please complete all requirements`
            }
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserDeclaredQuestions;
